import React from 'react';

import type { RouteMatch } from '@confluence/route/entry-points/Route';
import {
	CONTAINER_HOME_ID,
	COMPANY_HUB_SETTINGS_ID,
} from '@confluence/app-navigation/entry-points/navigationIDs';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { ContentHistoryLoader } from '@confluence/content-history';
import { NewContentHistory } from '@confluence/content-history-new/entry-points/NewContentHistory';
import { getCompanyHubPseudoAppRoute } from '@confluence/ssr-app/entry-points/getCompanyHubPseudoAppRoute';
import { expValEquals } from '@confluence/feature-experiments';
import { ClassicEditorContextProviderLoader } from '@confluence-classic/confluence-fabric-editor/src/components/ClassicEditorContextProvider';

const parseVersionParam = (value: string | string[] | undefined): number | undefined => {
	if (!value || Array.isArray(value)) return undefined;
	const parsedValue = parseInt(value, 10);
	return isNaN(parsedValue) ? undefined : parsedValue;
};

const ContentHistoryRouteComponent = ({ params: { spaceKey, contentId }, query }: RouteMatch) => {
	const version1 = parseVersionParam(query?.version1);
	const version2 = parseVersionParam(query?.version2);

	return expValEquals('confluence_content_history_updates', 'isEnabled', true) ? (
		<ClassicEditorContextProviderLoader>
			<NewContentHistory
				contentId={contentId}
				spaceKey={spaceKey}
				ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
				version1={version1}
				version2={version2}
			/>
		</ClassicEditorContextProviderLoader>
	) : (
		<ContentHistoryLoader spaceKey={spaceKey} contentId={contentId} stack="classic" />
	);
};

ContentHistoryRouteComponent.NAVIGATION_PARAMS = ({
	params: { spaceKey, contentId, contentSlug },
}: RouteMatch) => {
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	return {
		Screen: {
			screenEvent: {
				name: isCompanyHub ? 'hubHistoryScreen' : 'pageHistoryScreen',
				id: contentId,
			},
			pageState: {
				spaceKey,
				contentId,
				contentSlug,
			},
		},
		MainLayout: {
			spaceKey,
			navView: isCompanyHub ? COMPANY_HUB_SETTINGS_ID : CONTAINER_HOME_ID,
		},
	};
};

export const ContentHistoryRoute = getCompanyHubPseudoAppRoute(
	ContentHistoryRouteComponent,
	(route: RouteMatch) => isCompanyHubSpaceKey(route?.params?.spaceKey),
);
